import { defineStore } from 'pinia'
import { i18n } from '@/i18n'
import { createSignupSession } from '@/api/medUsers'
import { ANALYTIC_AGE_TO_AGE_RANGE_MAP, VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY } from '@/helpers/constants'
import { round } from '@/helpers/utils'
import { capitalize } from 'lodash-es'
import { convertHeight, convertWeights } from '@/helpers/userData'
import * as Sentry from '@sentry/vue'
import { useMainStore } from './main'
import { useLocationStore } from './location'
import { useFeatureFlagsStore } from './featureFlags'
import { asMedicationKind } from '@/domain/medication'
import { weeklyStorage, sessionOrPreviewStorage, sharedKeyPersist } from './persistence'

// Vue 2 backward-compat: vuex-persistedstate used to restore ALL keys from the
// sessionStorage blob regardless of configured paths, so tests that set
// sessionStorage['vuex'].survey accidentally worked even though 'survey' wasn't
// in the sessionStorage plugin's paths. Pinia doesn't do this — we replicate
// the behaviour by patching from sessionStorage in afterRestore.
// For real users, sessionStorage['vuex'] never contains a 'survey' key, so this is a no-op.
function _patchFromSessionStorage(context) {
	try {
		const raw = sessionOrPreviewStorage.getItem('vuex')
		if (!raw) return
		const blob = JSON.parse(raw)
		if (blob?.survey && Object.keys(blob.survey).length > 0) {
			context.store.$patch(blob.survey)
		}
	} catch {
		/* ignore */
	}
}

export const useSurveyStore = defineStore('survey', {
	state: () => ({
		values: {
			height: null,
			weight: null,
			targetWeight: null,
			sex: '',
			consents: [],
			dateEnd: null,

			data: {
				health_conditions: [],
				eating_disorder: [],
				pregnancy: [],
				measurement_system: null,
				measurement_system_height: null,
				measurement_system_weight: null,
				max_weight: null,
				weight_loss_goals: [],
				gender_identity: null,
				non_binary_identity: null,
			},
		},
		model: {},
		analyticData: {
			userStepCount: 1,
			onboardingStepCount: 1,
		},
		weightPredicts: [],

		// Medication the user explicitly picked on the CreatedProgramView "other options"
		// screen (semaglutide | tirzepatide). Kept SEPARATE from the funnel answer
		// `values.data.specific_medication_choice` — that retains the raw choice (incl.
		// "recommend_me") for analytics — and outside the onboarding-builder model, so the
		// finish-time model re-sync can't clobber it. Drives paywall routing via
		// `effectiveMedicationChoice`.
		selectedMedication: null,
	}),

	getters: {
		getPropertyValue:
			(state) =>
			(property, isData = false, legacyAnalytics = false) => {
				let values = isData ? state.values.data[property] : state.values[property]
				if (legacyAnalytics && property in VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY) {
					try {
						if (Array.isArray(values)) {
							values = values.map((v) => {
								return VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY[property][v] ?? v
							})
						} else {
							values = VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY[property][values] ?? values
						}
					} catch (e) {
						Sentry.captureException(e)
					}
				}
				return values
			},

		// Effective medication for routing/content — ALWAYS resolves to a concrete kind
		// ('semaglutide' | 'tirzepatide'), never undefined. Read by the paywall router
		// (location.getPaywallRoute), the paywall itself (PaywallView.recommendedMedication)
		// and the Braze `medication` attribute. Priority:
		//   1. `selectedMedication` — the user's explicit pick on CreatedProgramView.
		//   2. on an area with `options.medicationFromLanding` — the landing variant the
		//      creative picked, and NOTHING else (DEV-35776, see below).
		//   3. `specific_medication_choice` (single-select → array) when it's already one of
		//      the two concrete medications.
		//   4. otherwise (any other answer — "recommend me", unknown, or no answer) the remote
		//      feature flag decides; absent/unknown flag → tirzepatide.
		// Matched on membership, NOT the "recommend_me" literal, so renaming/adding that option
		// keeps working and the result can never be undefined. The fallback comes from the
		// featureFlags store: `hydrate()` (router.beforeEach) reads the remote
		// `recommended_medication` flag into `getDefaultMedicationKind`.
		effectiveMedicationChoice(state) {
			if (state.selectedMedication) return state.selectedMedication

			const fallback = useFeatureFlagsStore().getDefaultMedicationKind
			const locationStore = useLocationStore()

			// DEV-35776: где вариант лендинга выбирает сам креатив, он же — ЕДИНСТВЕННЫЙ
			// источник препарата: пришедший с тирзепатидного креатива не должен уехать на
			// семаглутидный пейвол, нажав «Affordability». Ответ по-прежнему пишется в
			// `values.data` и уходит в аналитику — он просто перестаёт участвовать в маршруте.
			// Читаем результат резолва лендинга, а не `utm_term`: буквы креатива разбираются
			// один раз, в area-конфиге. Универсальный лендинг оставляет null → тот же дефолт,
			// что и ниже.
			if (locationStore.getAppAreaConfig?.options?.medicationFromLanding) {
				return locationStore.landingMedication ?? fallback
			}

			const raw = state.values.data.specific_medication_choice
			const choice = Array.isArray(raw) ? raw[0] : raw

			return asMedicationKind(choice) ?? fallback
		},

		userWeightInKg: (state) => {
			return state.values.weight
		},

		userTargetWeightInKg: (state) => {
			return state.values.targetWeight
		},

		userWeightByUnitSystem(state) {
			const mainStore = useMainStore()
			return convertWeights(state.values.weight, mainStore.getWeightUnitSystem)
		},

		userTargetWeightByUnitSystem(state) {
			const mainStore = useMainStore()
			return convertWeights(state.values.targetWeight, mainStore.getWeightUnitSystem)
		},

		userHeightByUnitSystem(state) {
			const mainStore = useMainStore()
			return convertHeight(state.values.height, mainStore.getHeightUnitSystem)
		},

		userHeightInCm: (state) => state.values.height,

		userSex() {
			const sex = this.getPropertyValue('sex')

			if (!sex) {
				const error = new Error('User sex is not defined')
				Sentry.captureException(error, {
					extra: { sex: String(sex) },
					tags: { requiredUserProperties: true },
				})

				return 'female'
			}

			return sex
		},

		userAge() {
			return this.getPropertyValue('age', true)
		},

		ageRange() {
			return ANALYTIC_AGE_TO_AGE_RANGE_MAP[this.userAge] ?? 'other'
		},

		getUserHasConsent(state) {
			return (consent) => {
				try {
					return state.values.consents.includes(consent)
				} catch {
					return false
				}
			}
		},

		// Whether the store already holds everything POST /signup-session hard-requires:
		// positive biometrics and a measurement system (med-users rejects 0/null with
		// 400/422). All of it is collected well before the enter-email screen in every
		// GLP funnel, but a deep link straight into /ob/enter-email on a cold store
		// would post junk — the early-create call site (EmailForm) must check this
		// first. The finish-time call posts unconditionally: a failure there is a real
		// bug that must surface in monitoring, not be skipped.
		canCreateSignupSession(state) {
			const rawMeasurementSystem = state.values.data.measurement_system
			const measurementSystem = Array.isArray(rawMeasurementSystem) ? rawMeasurementSystem[0] : rawMeasurementSystem

			return (
				Math.round(state.values.height) > 0 &&
				Math.round(state.values.weight) > 0 &&
				Math.round(state.values.targetWeight) > 0 &&
				Boolean(measurementSystem)
			)
		},

		getUserStepCount(state) {
			return state.analyticData.userStepCount
		},

		getOnboardingStepCount(state) {
			return state.analyticData.onboardingStepCount
		},

		brazeAttributes() {
			const mainStore = useMainStore()
			const locationStore = useLocationStore()
			let gender = 'u'

			if (this.userSex === 'female') {
				gender = 'f'
			} else if (this.userSex === 'male') {
				gender = 'm'
			}

			return {
				external_id: mainStore.getUserId,
				email: mainStore.getUserEmail,
				age: this.userAge,
				gender: gender,
				region_name: mainStore.getGeo.regionName ? capitalize(mainStore.getGeo.regionName) : null,
				country: mainStore.getCountryCodeUppercase,
				language: window.language.substr(0, 2),
				custom_attrs: {
					funnel: locationStore.getAppAreaConfig.funnel.slug,
					initial_funnel: locationStore.getInitialFunnelType,
					currency: mainStore.getCurrency,
					height: this.userHeightInCm ? round(this.userHeightInCm, 2) : null,
					weight: this.userWeightInKg ? round(this.userWeightInKg, 2) : null,
					original_weight: this.userWeightInKg ? round(this.userWeightInKg, 2) : null,
					target_weight: this.userTargetWeightInKg ? round(this.userTargetWeightInKg, 2) : null,
				},
			}
		},

		getWeightPredicts(state) {
			return state.weightPredicts
		},

		/**
		 * 3 predicts available in total (easy/normal/hard). Counts from 1 to 4:
		 * step 4 clamps to the last (hardest) predict since there's no 4th tier.
		 * implementation nuances is in WeightPredictor
		 */
		getWeightPredictDateByStep() {
			return (step) => {
				const predicts = this.getWeightPredicts
				if (!predicts?.length) return undefined
				const index = Math.min(step - 1, predicts.length - 1)
				return predicts[index]?.targetDate
			}
		},

		onboardingData(state) {
			const onboardingData = {}

			// Every value in onboardingData should be an array of strings or null
			Object.entries(state.values.data).forEach(([key, value]) => {
				if (Array.isArray(value) && value.length) {
					onboardingData[key] = value
				} else if (value) {
					onboardingData[key] = [String(value)]
				} else {
					onboardingData[key] = null
				}
			})

			return onboardingData
		},
	},

	actions: {
		updateSurveyModel(data) {
			this.model = data
		},

		/**
		 * @param data {Object}
		 * @param data.isData {Boolean}
		 * @param data.property {String}
		 * @param data.value {any}
		 */
		updateProperty(data) {
			if (data.isData) {
				this.values.data[data.property] = data.value
			} else {
				this.values[data.property] = data.value
			}
			this.changed = true
		},

		// Explicit medication pick from the CreatedProgramView "other options" screen.
		// Kept out of `values.data`/the builder model so the finish-time model re-sync
		// doesn't overwrite it, and so `specific_medication_choice` stays intact for analytics.
		setSelectedMedication(medication) {
			this.selectedMedication = medication
		},

		addUserConsent(consent) {
			const consents = this.values.consents
			if (!consents.includes(consent)) {
				consents.push(consent)
			}
		},
		removeUserConsent(consent) {
			const consents = this.values.consents
			const index = consents.indexOf(consent)
			if (index !== -1) {
				consents.splice(index, 1)
			}
		},

		incrementUserStep() {
			this.analyticData.userStepCount += 1
		},

		setOnboardingStepCount(count) {
			this.analyticData.onboardingStepCount = count
		},

		resetAnalyticDataCounter() {
			this.analyticData.userStepCount = 1
			this.analyticData.onboardingStepCount = 1
		},

		setWeightPredicts(data) {
			this.weightPredicts = data
		},

		// POST /signup-session is an idempotent upsert on the backend (always 200, never
		// 409 — med-users #65): a re-POST overwrites the create-owned survey/consents
		// data and never touches PATCH-written contact fields. It runs twice per GLP
		// run: at the enter-email screen (early create, so a CRM retargeting email can
		// only ever carry a session_id that exists on the backend — DEV-34792) and at
		// onboarding finish (re-POST backfills the post-email answers into `data`).
		async saveSurveyData(sentryContext) {
			const mainStore = useMainStore()
			const KOVA_CONSENT_TYPES = ['health_data_processing', 'terms_and_conditions', 'marketing']

			// The backend's CreateSignupSessionRequest.sex enum only accepts "female"/"male".
			// Real users always resolve to one before this call (both callers — the enter-email
			// screen and onboarding finish — run after the required g-input + personal-program
			// gender screens). But the survey seeds the "gs" sentinel ("ask on g-input"), and any
			// path that skips those screens — mock-data, or a rare navigation gap leaving
			// "prefer_not_to_say" — would otherwise 400 the whole session-create and dead-end the
			// user post-onboarding.
			// Coerce to the same default the userSex getter uses and flag it, so a real
			// occurrence surfaces instead of failing the request.
			let sex = this.values.sex
			if (!['female', 'male'].includes(sex)) {
				sex = 'female'
			}

			const response = await createSignupSession(
				{
					session_id: mainStore.sessionId,
					height: Math.round(this.values.height),
					weight: Math.round(this.values.weight),
					target_weight: Math.round(this.values.targetWeight),
					// seconds_from_gmt: -new Date().getTimezoneOffset() * 60,
					timezone: Intl?.DateTimeFormat?.().resolvedOptions?.()?.timeZone ?? null,
					sex,
					country: mainStore.getCountryCodeUppercase,
					language: i18n.global.locale?.value || i18n.locale || 'en',
					measurement_system: Array.isArray(this.values.data.measurement_system)
						? this.values.data.measurement_system[0]
						: this.values.data.measurement_system,
					consents: this.values.consents.filter((c) => KOVA_CONSENT_TYPES.includes(c)),
					// Store the email on the session at create (med-users #139). This is what lets
					// a CRM RETARGETING user who returns to the paywall with an empty store still
					// complete signup: the shipping PATCH omits email and the backend carries this
					// stored value forward. EmailForm runs before onboarding finish, so it's set by
					// now; omitted (optional field) when absent to avoid an EmailAddress format
					// rejection on an empty string.
					email: mainStore.email || undefined,
					data: this.values.data,
				},
				sentryContext,
			)

			return response
		},

		async updateSurveyData(data) {
			// Legacy FSTR `api.updateUser` was removed (the med funnel has no such backend).
			// Return the merged shape so existing callers that chain `setUserData(user)`
			// continue to function without making a backend call.
			return {
				...this.values,
				name: data?.name,
				email: data?.email,
			}
		},
	},

	persist: {
		...sharedKeyPersist('vuex', 'survey', weeklyStorage),
		afterRestore: _patchFromSessionStorage,
	},
})
